import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';

const STORAGE_KEY = "ems-color-mode";

const ColorModeContext = createContext({ mode: "light", toggleMode: () => { } });

export const useColorMode = () => useContext(ColorModeContext);

const loadMode = () => {
  try {
    return localStorage.getItem(STORAGE_KEY) === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
};

// Light / dark mode for the dashboard. Sets data-theme on <html> so the CSS colour tokens
// in index.css switch (portals like menus and tooltips live under <body>, so <html> is used),
// and gives MUI components a matching palette. Removed on unmount so the login pages stay light.
const ColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState(loadMode);

  const toggleMode = useCallback(() => {
    setMode(prev => {
      const next = prev === "dark" ? "light" : "dark";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // Storage unavailable - the choice just won't persist
      }
      return next;
    });
  }, []);

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute("data-theme", mode);
    root.style.colorScheme = mode;
    return () => {
      root.removeAttribute("data-theme");
      root.style.colorScheme = "";
    };
  }, [mode]);

  const theme = useMemo(() => createTheme({
    palette: mode === "dark"
      ? {
        mode: "dark",
        background: { default: "#0A0F1C", paper: "#111827" },
        divider: "#273449",
      }
      : { mode: "light" },
  }), [mode]);

  const value = useMemo(() => ({ mode, toggleMode }), [mode, toggleMode]);

  return (
    <ColorModeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ColorModeProvider;
